import React, { useEffect, useState } from "react"
import {
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@mui/material"
import { CommunicationType, CreateTerminal, LockerSize, TerminalStatus, TerminalType } from "../../infrastructure/models/transport/request/createterminal"
import { Duration } from "../../infrastructure/models/common/Duration"
import { PaymentType } from "../models/enum/PaymentType"

const TerminalForm = ({ onSubmit, initialValues, apiError }: { onSubmit: (data: any) => void, initialValues: any, apiError?: { code?: string, message?: string } }) => {
  const [formData, setFormData] = useState<CreateTerminal>({
    franchiseId: initialValues.franchiseId,
    name: "",
    latitude: 0,
    longitude: 0,
    status: TerminalStatus.ACTIVE,
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
    },
    basePrice: {
      duration: Duration.MINUTE,
      time: 0,
      prices: [
        { size: LockerSize.SMALL, price: 0 },
        { size: LockerSize.MEDIUM, price: 0 },
        { size: LockerSize.LARGE, price: 0 },
        { size: LockerSize.X_L, price: 0 },
        { size: LockerSize.CUSTOM, price: 0 },
      ],
    },
    additionalPrices: {
      duration: Duration.MINUTE,
      time: 0,
      operator: "+",
      prices: [
        { size: LockerSize.SMALL, price: 0 },
        { size: LockerSize.MEDIUM, price: 0 },
        { size: LockerSize.LARGE, price: 0 },
        { size: LockerSize.X_L, price: 0 },
        { size: LockerSize.CUSTOM, price: 0 },
      ],
    },
    lockers: [
      {
        size: LockerSize.SMALL,
        count: 0,
        dimensions: { width: 150, height: 150, depth: 200 },
      },
      {
        size: LockerSize.MEDIUM,
        count: 0,
        dimensions: { width: 160, height: 180, depth: 200 },
      },
      {
        size: LockerSize.LARGE,
        count: 0,
        dimensions: { width: 180, height: 200, depth: 200 },
      },
      {
        size: LockerSize.X_L,
        count: 0,
        dimensions: { width: 200, height: 220, depth: 200 },
      },
      {
        size: LockerSize.CUSTOM,
        count: 0,
        dimensions: { width: 250, height: 250, depth: 200 },
      },
    ],
    bluetoothMacId: "",
    dropPaymentRequired: false,
    pickupLoginRequired: false,
    supportedCountries: [{ name: "India", code: "+91" }],
    supportNumber: "",
    hardwareCommunication: CommunicationType.BLUETOOTH,
    terminalType: TerminalType.FRESHKART,
    allowedManualSessionsPerMonth: 20,
    paymentType: PaymentType.DURING_DROP_OFF,
    videos: [
      {
        source: "",
        description: "",
        isActive: true
      }
    ]
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const validateBeforeSubmit = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = "Terminal Name is required"
    }

    if (!formData.bluetoothMacId.trim()) {
      newErrors.bluetoothMacId = "Bluetooth MAC ID is required"
    }

    if (!formData.supportNumber || formData.supportNumber.length < 13) {
      newErrors.supportNumber = "Support number is required"
    }

    // -------- ADDRESS VALIDATION --------
    if (!formData.address.street.trim()) {
      newErrors["address.street"] = "Street is required"
    }

    if (!formData.address.city.trim()) {
      newErrors["address.city"] = "City is required"
    }

    if (!formData.address.state.trim()) {
      newErrors["address.state"] = "State is required"
    }

    if (!formData.address.postalCode.trim()) {
      newErrors["address.postalCode"] = "Postal code is required"
    } else if (!/^\d{6}$/.test(formData.address.postalCode)) {
      newErrors["address.postalCode"] = "Postal code must be 6 digits"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target

    setFormData((prevData) => {
      // Handle nested address fields
      if (name.startsWith("address.")) {
        const field = name.split(".")[1]
        return {
          ...prevData,
          address: {
            ...prevData.address,
            [field]: value,
          },
        }
      }

      // Handle basePrice.prices and additionalPrices.prices updates
      else if (
        name.startsWith("basePrice.prices") ||
        name.startsWith("additionalPrices.prices")
      ) {
        const [parent, field, index, subfield] = name.split(".") // subfield should be 'price'
        const priceIndex = parseInt(index, 10)
        const newPrices = [...prevData[parent][field]]
        newPrices[priceIndex] = {
          ...newPrices[priceIndex],
          [subfield]: Number(value),
        }
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            [field]: newPrices,
          },
        }
      }

      // Handle basePrice.time and additionalPrices.time
      else if (name === "basePrice.time" || name === "additionalPrices.time") {
        const [parent] = name.split(".")
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            time: value,
          },
        }
      }

      // Handle basePrice.duration and additionalPrices.duration
      else if (name === "basePrice.duration" || name === "additionalPrices.duration") {
        const [parent] = name.split(".")
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            duration: value as Duration,
          },
        }
      }

      // Handle additionalPrices.operator
      else if (name === "additionalPrices.operator") {
        return {
          ...prevData,
          additionalPrices: {
            ...prevData.additionalPrices,
            operator: value as "+" | "-" | "*" | "/",
          },
        }
      }

      // Handle locker count and dimensions
      else if (name.startsWith("lockers")) {
        const parts = name.split(".")
        const index = parseInt(parts[1], 10)
        const key = parts[2]

        const lockers = [...prevData.lockers]

        // If updating dimensions
        if (key === "dimensions") {
          const dimKey = parts[3] // e.g., width, height, depth
          lockers[index] = {
            ...lockers[index],
            dimensions: {
              ...lockers[index].dimensions,
              [dimKey]: Number(value),
            },
          }
        } else {
          // Updating count or other top-level locker fields
          lockers[index] = {
            ...lockers[index],
            [key]: key === "count" ? Number(value) : value,
          }
        }

        return {
          ...prevData,
          lockers,
        }
      }

      else if (name.startsWith("videos")) {
        const [_, index, field] = name.split(".")
        const videoIndex = parseInt(index, 10)

        const updatedVideos = [...(prevData.videos || [])]

        updatedVideos[videoIndex] = {
          ...updatedVideos[videoIndex],
          [field]: field === "isActive" ? value === "true" : value
        }

        return {
          ...prevData,
          videos: updatedVideos
        }
      }

      // All other direct fields
      else {
        return {
          ...prevData,
          [name]: value,
        }
      }
    })
  }

  return (
    <Box
      sx={{
        p: 4,
        border: "1px solid #ccc",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <Typography variant="h5" gutterBottom>
        Franchise Id: {formData.franchiseId}
      </Typography>

      <Typography variant="h5" gutterBottom>
        Add Terminal
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <TextField
            fullWidth
            label="Latitude"
            name="latitude"
            required
            value={formData.latitude}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <TextField
            fullWidth
            label="Longitude"
            name="longitude"
            required
            value={formData.longitude}
            onChange={handleChange}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <TextField
            select
            fullWidth
            label="Status"
            name="status"
            required
            value={formData.status}
            onChange={handleChange}
          >
            {Object.keys(TerminalStatus).map((key) => {
              if (isNaN(Number(key))) {
                // Displaying the string without leading/trailing spaces
                const label = key.charAt(0) + key.slice(1).toLowerCase()
                return (
                  <MenuItem key={key} value={TerminalStatus[key]}>
                    {label}
                  </MenuItem>
                )
              }
            })}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Street"
            name="address.street"
            required
            value={formData.address.street}
            onChange={handleChange}
            error={!!errors["address.street"]}
            helperText={errors["address.street"]}
          />
        </Grid>
        <Grid size={{ xs: 5 }}>
          <TextField
            fullWidth
            label="City"
            name="address.city"
            required
            value={formData.address.city}
            onChange={handleChange}
            error={!!errors["address.city"]}
            helperText={errors["address.city"]}
          />
        </Grid>
        <Grid size={{ xs: 5 }}>
          <TextField
            fullWidth
            label="State"
            name="address.state"
            required
            value={formData.address.state}
            onChange={handleChange}
            error={!!errors["address.state"]}
            helperText={errors["address.state"]}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <TextField
            fullWidth
            label="Postal Code"
            name="address.postalCode"
            required
            value={formData.address.postalCode}
            onChange={handleChange}
            slotProps={{
              htmlInput: { maxLength: 6 }
            }}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
            error={!!errors["address.postalCode"]}
            helperText={errors["address.postalCode"]}
          />
        </Grid>

        {formData.lockers.map((locker, index) => (
          <React.Fragment key={locker.size}>
            {/* Locker Count */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label={`${locker.size} Locker Count`}
                name={`lockers.${index}.count`}
                value={locker.count}
                onChange={handleChange}
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>

            {/* Width */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label={`${locker.size} Width (cm)`}
                name={`lockers.${index}.dimensions.width`}
                value={locker.dimensions.width}
                onChange={handleChange}
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>

            {/* Height */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label={`${locker.size} Height (cm)`}
                name={`lockers.${index}.dimensions.height`}
                value={locker.dimensions.height}
                onChange={handleChange}
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>

            {/* Depth */}
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label={`${locker.size} Depth (cm)`}
                name={`lockers.${index}.dimensions.depth`}
                value={locker.dimensions.depth}
                onChange={handleChange}
                inputProps={{ inputMode: "numeric" }}
              />
            </Grid>
          </React.Fragment>
        ))}

        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Small Locker Price"
            name="basePrice.prices.0.price"
            required
            value={formData.basePrice.prices[0].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Medium Locker Price"
            name="basePrice.prices.1.price"
            required
            value={formData.basePrice.prices[1].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Large Locker Price"
            name="basePrice.prices.2.price"
            required
            value={formData.basePrice.prices[2].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="X_L Locker Price"
            name="basePrice.prices.3.price"
            required
            value={formData.basePrice.prices[3].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Custom Locker Price"
            name="basePrice.prices.4.price"
            required
            value={formData.basePrice.prices[4].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>

        {/* Base Price Duration & Time */}
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField
            fullWidth
            label="Base Price Time"
            name="basePrice.time"
            value={formData.basePrice.time}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (
                !/^[0-9]$/.test(e.key) &&
                !allowedKeys.includes(e.key)
              ) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Base Price Duration"
            name="basePrice.duration"
            value={formData.basePrice.duration}
            onChange={handleChange}
          >
            {Object.values(Duration).map((d) => (
              <MenuItem key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Additional Prices */}
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Small Additional Price"
            name="additionalPrices.prices.0.price"
            required
            value={formData.additionalPrices.prices[0].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Medium Additional Price"
            name="additionalPrices.prices.1.price"
            required
            value={formData.additionalPrices.prices[1].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Large Additional Price"
            name="additionalPrices.prices.2.price"
            required
            value={formData.additionalPrices.prices[2].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="X_L Additional Price"
            name="additionalPrices.prices.3.price"
            required
            value={formData.additionalPrices.prices[3].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 2.4 }}>
          <TextField
            fullWidth
            label="Custom Additional Price"
            name="additionalPrices.prices.4.price"
            required
            value={formData.additionalPrices.prices[4].price}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>

        {/* Additional Price Duration & Time */}
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField
            fullWidth
            label="Additional Price Time"
            name="additionalPrices.time"
            value={formData.additionalPrices.time}
            onChange={handleChange}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (
                !/^[0-9]$/.test(e.key) &&
                !allowedKeys.includes(e.key)
              ) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Additional Price Duration"
            name="additionalPrices.duration"
            value={formData.additionalPrices.duration}
            onChange={handleChange}
          >
            {Object.values(Duration).map((d) => (
              <MenuItem key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Bluetooth Mac ID */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Bluetooth MAC ID"
            name="bluetoothMacId"
            required
            value={formData.bluetoothMacId}
            onChange={handleChange}
            error={!!errors.bluetoothMacId}
            helperText={errors.bluetoothMacId}
          />
        </Grid>
        {/* Other Checkbox Fields */}
        <Grid size={{ xs: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.dropPaymentRequired}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dropPaymentRequired: e.target.checked,
                  })
                }
                name="dropPaymentRequired"
              />
            }
            label="Drop Payment Required"
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.pickupLoginRequired}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    pickupLoginRequired: e.target.checked,
                  })
                }
                name="pickupLoginRequired"
              />
            }
            label="Pickup Login Required"
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            select
            fullWidth
            label="Payment Type"
            name="paymentType"
            required
            value={formData.paymentType}
            onChange={handleChange}
          >
            {Object.values(PaymentType).map((value) => {
              const label = value.trim()
              return (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              )
            })}
          </TextField>
        </Grid>

        <Grid size={{ xs: 4 }}>
          <TextField
            select
            fullWidth
            label="Terminal Type"
            name="terminalType"
            required
            value={formData.terminalType}
            onChange={handleChange}
          >
            {Object.values(TerminalType).map((value) => {
              const label = value.trim()
              return (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              )
            })}
          </TextField>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TextField
            select
            fullWidth
            label="Communication Type"
            name="hardwareCommunication"
            required
            value={formData.hardwareCommunication}
            onChange={handleChange}
          >
            {Object.values(CommunicationType).map((value) => {
              const label = value.trim()
              return (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              )
            })}
          </TextField>
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TextField
            fullWidth
            label="Allowed Manual Sessions/Month"
            name="allowedManualSessionsPerMonth"
            required
            value={formData.allowedManualSessionsPerMonth}
            onChange={handleChange}
            slotProps={{
              htmlInput: { maxLength: 3 }
            }}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab',
              ]
              if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault()
              }
            }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="h6">Videos</Typography>
        </Grid>

        {formData.videos?.map((video, index) => (
          <React.Fragment key={index}>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                label="Video URL"
                name={`videos.${index}.source`}
                value={video.source}
                onChange={handleChange}
              />
            </Grid>

            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                label="Description"
                name={`videos.${index}.description`}
                value={video.description}
                onChange={handleChange}
              />
            </Grid>

            <Grid size={{ xs: 2 }}>
              <TextField
                select
                fullWidth
                label="Active"
                name={`videos.${index}.isActive`}
                value={video.isActive.toString()}
                onChange={handleChange}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 2 }}>
              <Button
                color="error"
                onClick={() => {
                  const updated = [...formData.videos]
                  updated.splice(index, 1)
                  setFormData({ ...formData, videos: updated })
                }}
              >
                Remove
              </Button>
            </Grid>
          </React.Fragment>
        ))}

        <Grid size={{ xs: 12 }}>
          <Button
            variant="outlined"
            onClick={() =>
              setFormData({
                ...formData,
                videos: [
                  ...(formData.videos || []),
                  { source: "", description: "", isActive: true }
                ]
              })
            }
          >
            Add Video
          </Button>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            required
            label="Support Number"
            name="supportNumber"
            value={formData.supportNumber.replace("+91", "")}
            onChange={(e) => {
              const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10)
              setFormData((prev) => ({
                ...prev,
                supportNumber: `+91${onlyDigits}`
              }))
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">+91</InputAdornment>
            }}
            inputProps={{
              maxLength: 10,
              pattern: "\\d{10}"
            }}
            error={!!errors["supportNumber"]}
            helperText={errors["supportNumber"]}
          />
        </Grid>

      </Grid>
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        onClick={() => {
          const isValid = validateBeforeSubmit()
          if (!isValid) return
          onSubmit(formData)
        }}
      >
        Submit
      </Button>
    </Box>
  )
}

export default TerminalForm
